import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { Droppable } from "react-beautiful-dnd";

import { setExtendableId, setExtendableType } from "../../store/actions/edit";
import { deleteStop } from "../../store/actions/custom";
import DragItem from "./DragItem";
import { setHoverStop } from "../../store/actions/map";
import {
  setDragSelection,
  setDragSelectionByArr,
  setManualDragSelection
} from "../../store/actions/common";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import UnsavedChangesDialog from "../dialogs/UnsavedChangesDialog";
import { store } from "../../index";
import DraggablesWrapper from "./DraggablesWrapper";

// Check DragItem props, see if you can make it easy

function DragList({
  projectStatus,
  published,
  projectId,
  route,
  deleteStop,
  driverDragging,
  setExtendableId,
  setExtendableType,
  setHoverStop,
  setDragSelection,
  setManualDragSelection,
  plan,
  color,
  hideStart,
  showReload,
  nextStart,
  countStart,
  selectedStops,
  toggleStopSelect,
  disableDrag,
  setDragSelectionByArr
}) {
  const [items, setItems] = useState(route.stops);
  const [confirmDialogState, setConfirmDialogState] = useState({
    open: false,
    index: null,
    isEditing: false,
    stop: {}
  });

  const [changesDialog, setChangesDialog] = useState({ visible: false });

  // Open edit panel for stop
  const editStop = useCallback(
    (id, status) => {
      /*
      if (!["finished", "skipped"].includes(status))
        setManualDragSelection(`route-${route.id}`, [id]);
      else setManualDragSelection(``, []);
      */
      setExtendableId(id);
      setExtendableType("stops", { status });
    },
    [route.id, setManualDragSelection, setExtendableId, setExtendableType]
  );

  // If unsaved changes, notify before invoking editStop function
  const warnEdit = useCallback(
    (e, id, type, isEditing, status) => {
      e.stopPropagation();
      if (!isEditing) {
        if (!store.getState().extendable.saveDisabled) {
          setChangesDialog({ visible: true, id, status });
        } else {
          if (type === "break") {
            setManualDragSelection(`route-${route.id}`, [id]);
            if (!published) {
              setExtendableId(route.vehicle_id);
              setExtendableType("vehicles", { isBreak: true });
            }
          } else {
            editStop(id, status);
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [route.id, published, editStop, route.vehicle_id]
  );

  // Draggable list styles
  const getItemStyle = useCallback(
    (isDragging, draggableStyle, index) => ({
      // some basic styles to make the items look a bit nicer
      userSelect: "none",
      backgroundColor: "#fff",

      // zIndex: items.length - index,

      // styles we need to apply on draggables
      ...draggableStyle
    }),
    []
  );

  const handleItemClick = useCallback(
    (listId, itemId) => e => {
      if (
        !store.getState().extendable.saveDisabled &&
        itemId !== store.getState().extendable.id
      ) {
        setChangesDialog({ visible: true, isDragSelection: true });
      } else {
        setDragSelection(listId, itemId, e.ctrlKey || e.metaKey, e.shiftKey);
      }
    },
    [setDragSelection]
  );

  const handleMouseEnter = useCallback(
    stop => {
      setHoverStop({ stop, routeId: route.id, routeName: route.route_name });
    },
    [route.id, setHoverStop]
  );

  const handleMouseLeave = useCallback(
    () => setHoverStop(null),
    [setHoverStop]
  );

  // Count - used to show stop sequence number
  let count = 0;

  const getListStyle = isDraggingOver => ({
    width: "100%",
    minHeight: 10
  });

  let daysPassed = false;

  useEffect(() => {
    setItems(route.stops);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route]);

  return (
    <>
      <div style={{ width: "100%" }}>
        {/* Start stop */}
        {!hideStart && (
          <DragItem
            isStartEnd
            item={route.stops[0]}
            index={0}
            container
            routeStatus={route.status}
            projectStatus={projectStatus}
            alignItems="center"
            onMouseEnter={() => handleMouseEnter(route.stops[0])}
            onMouseLeave={handleMouseLeave}
            color={color}
          />
        )}

        {/* Services can be dragged if - 
          - project isn't published 
          - route is not locked 
          - not using disabled live update feature 
          */}
        {published ||
        route.locked ||
        !items[0].id ||
        (route.status === "in_progress" &&
          !(plan && plan.ft_adjust_live_routes)) ? (
          items
            .filter(el => el.type !== "start" && el.type !== "end")
            .map((item, index) => {
              // Calculate count
              if (item.type !== "break") count++;
              let number = count;
              if (item.days_on_the_road >= 1) daysPassed = true;
              let daysPassedInner = daysPassed;
              return (
                <DragItem
                  key={item.id}
                  onClick={e =>
                    // Allow edit only stops
                    item.type !== "break" &&
                      warnEdit(e, item.id, item.type, undefined, item.status)
                  }
                  item={item}
                  index={index}
                  count={countStart + number}
                  routeStatus={route.status}
                  editStop={warnEdit}
                  deleteStop={setConfirmDialogState}
                  locked={route.locked}
                  daysPassed={daysPassedInner}
                  container
                  startedRoute={route.stops[0].status === "finished"}
                  projectStatus={
                    route.status === "in_progress" &&
                    !(plan && plan.ft_adjust_live_routes)
                      ? "published"
                      : projectStatus
                  }
                  alignItems="center"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  projectId={projectId}
                  color={color}
                  selected={selectedStops.includes(item.id)}
                  hasSelected={selectedStops.length > 0}
                  handleSelect={toggleStopSelect}
                  selectedStops={selectedStops}
                />
              );
            })
        ) : (
          <Droppable
            droppableId={`route-${route.id}`}
            isDropDisabled={driverDragging}
          >
            {(provided, snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={getListStyle(snapshot.isDraggingOver)}
                className="drag-list"
              >
                <DraggablesWrapper
                  items={items}
                  getItemStyle={getItemStyle}
                  warnEdit={warnEdit}
                  handleItemClick={handleItemClick}
                  route={route}
                  setConfirmDialogState={setConfirmDialogState}
                  projectId={projectId}
                  projectStatus={projectStatus}
                  handleMouseEnter={handleMouseEnter}
                  handleMouseLeave={handleMouseLeave}
                  color={color}
                  handleSelect={toggleStopSelect}
                  selectedStops={selectedStops}
                  countStart={countStart}
                  disableDrag={disableDrag}
                  setDragSelectionByArr={setDragSelectionByArr}
                />
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        )}
        {/* End stop */}
        {showReload && (
          <DragItem
            item={{ ...route.stops[route.stops.length - 1], type: "reload" }}
            nextStart={nextStart}
            index={route.stops.length - 1}
            container
            routeStatus={route.status}
            daysPassed={items.some(item => item.days_on_the_road >= 1)}
            projectStatus={projectStatus}
            endLocationDefined={route.end_location_defined}
            alignItems="center"
            onMouseEnter={() =>
              handleMouseEnter(route.stops[route.stops.length - 1])
            }
            onMouseLeave={handleMouseLeave}
            color={color}
          />
        )}
        {!showReload && (
          <DragItem
            isStartEnd
            item={route.stops[route.stops.length - 1]}
            index={route.stops.length - 1}
            container
            routeStatus={route.status}
            daysPassed={items.some(item => item.days_on_the_road >= 1)}
            projectStatus={projectStatus}
            endLocationDefined={route.end_location_defined}
            alignItems="center"
            onMouseEnter={() =>
              handleMouseEnter(route.stops[route.stops.length - 1])
            }
            onMouseLeave={handleMouseLeave}
            color={color}
          />
        )}
      </div>

      {/* creates dialog for every list, maybe needs to be moved to upper component */}
      <ConfirmDialog
        open={confirmDialogState.open}
        handleClose={() =>
          setConfirmDialogState({
            open: false,
            stop: { type: confirmDialogState.stop.type }
          })
        }
        action={() => deleteStop(confirmDialogState.stop)}
        title={`Delete ${
          confirmDialogState.stop.type === "break" ? "break" : "stop"
        }?`}
        body={`Deleting a ${
          confirmDialogState.stop.type === "break" ? "break" : "stop"
        } is permanent and cannot be undone.`}
      />

      <UnsavedChangesDialog
        open={changesDialog.visible}
        handleClose={() => setChangesDialog({ visible: false })}
        action={() => {
          if (changesDialog.isDragSelection) {
            setExtendableType(null);
            setExtendableId(null);
          } else {
            editStop(changesDialog.id, changesDialog.status);
          }
        }}
      />
    </>
  );
}

function mapStateToProps(state) {
  return {
    projectStatus: state.project.status,
    published: ["published", "expired", "finished"].includes(
      state.project.status.toLowerCase()
    ),
    driverDragging: state.common.draggingType === "drivers",
    plan:
      (state.auth.userData.organisationSite &&
        state.auth.userData.organisationSite.organisation.plan) ||
      {}
  };
}

export default connect(mapStateToProps, {
  setExtendableId,
  setExtendableType,
  setHoverStop,
  deleteStop,
  setDragSelection,
  setManualDragSelection,
  setDragSelectionByArr
})(DragList);
